import {
  CreditScoreFactor,
  CreditScoreGrade,
  CreditScoreNoScore,
  CreditScoreSuccess,
  CreditScoreTile,
  PerkSection,
  PerkSectionsEdge,
  Query,
  SdActionType,
  SectionItems,
  SectionStyleType,
  TileType,
} from '@varo/graphql-schema-types';
import { Linking } from 'react-native';
import GetHelpPartnerOffersQuery from 'services/graphql/queries/CreditScorePartnerOffersHelp.query';
import { FetchHomeTilesForIds } from 'services/graphql/queries/FetchHomeTilesV2';
import { CreditScoreTileStep } from 'shared/components/vxl/cards/CreditScoreCard/CreditScoreCard';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import { useQuery } from 'urql';

interface CreditScoreDataProps {
  hasCreditScore: boolean;
  creditScore: CreditScoreSuccess | CreditScoreNoScore;
  fillToStep: CreditScoreTileStep;
  factors: Array<CreditScoreFactor>;
  carouselOffers: PerkSectionsEdge;
  sectionData: SectionItems[] | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  goToLink: (link: string) => Promise<void>;
}

export const useCreditScoreData = (): CreditScoreDataProps => {
  const [{ data, fetching, error }, refetchBaseQuery] = useQuery<Query>({
    query: FetchHomeTilesForIds,
    variables: {
      ids: [HomeTileTypeIds.CREDIT_SCORE_TILE],
      tileTypes: [TileType.CreditScoreTile],
    },
    requestPolicy: 'cache-and-network',
  });
  const creditScoreDetails = data?.tiles?.find(
    (innerTile: CreditScoreTile) =>
      innerTile?.tileType === TileType.CreditScoreTile,
  ) as CreditScoreTile;

  const [{ data: partnerOfferData, fetching: partnerOfferDataFetching }] =
    useQuery<Query>({
      query: GetHelpPartnerOffersQuery,
      variables: {
        perkSectionsId: null,
        first: 20,
        after: null,
        perkSectionsInput: {
          sections: PerkSection.CreditScore,
        },
        perkSectionsItemsInput: {
          supportedActionTypes: [
            SdActionType.SdPartnerOfferDetailNavigationAction,
          ],
        },
      },
    });

  const creditScore = creditScoreDetails?.creditScore as CreditScoreSuccess;
  const hasCreditScore = creditScore?.gradeEnum !== CreditScoreGrade.NoScore;

  const fillToStep = (
    creditScore?.ranges?.findIndex(
      (item) => item?.description === creditScore?.grade,
    ) !== -1
      ? creditScore?.ranges?.findIndex(
          (item) => item?.description === creditScore?.grade,
        ) + 1
      : 0
  ) as CreditScoreTileStep;

  const factors = creditScore?.factors;
  const carouselOffers =
    partnerOfferData?.myPartnerOffers?.perkSections?.edges?.find(
      (edge) =>
        edge?.node?.sectionStyleType === SectionStyleType.MinimalGridCarousel,
    ) as PerkSectionsEdge;

  const sectionData: SectionItems[] | undefined =
    carouselOffers?.node?.items.map((item) => ({
      id: item?.id,
      title: item?.title,
      subTitle: item?.subTitle,
      offerType: item?.offerType,
      offerSource: item?.offerSource,
      cashback: item?.cashback,
      heroImage: item?.heroImage,
      logoImage: item?.logoImage,
      isNew: item?.isNew,
      isActivated: item?.isActivated ?? false,
      backgroundColor: item?.backgroundColor,
      merchantId: item?.merchantId,
      onClick: item?.onClick,
    }));

  const goToLink = async (link: string) => {
    const supported = await Linking.canOpenURL(link);

    if (supported) {
      await Linking.openURL(link);
    }
  };

  return {
    hasCreditScore,
    creditScore,
    fillToStep,
    factors,
    carouselOffers,
    sectionData,
    loading: fetching || partnerOfferDataFetching,
    error: error || (partnerOfferDataFetching ? null : null),
    refetch: refetchBaseQuery,
    goToLink,
  };
};
